import React, { useState, useEffect } from 'react';
import { ApiService } from '../services/api';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Chip,
  Tooltip,
  FormControlLabel,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  LinearProgress
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  ContentCopy as CopyIcon,
  Email as EmailIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  PersonAdd as PersonAddIcon,
  Send as SendIcon,
  Download as DownloadIcon,
  Print as PrintIcon
} from '@mui/icons-material';

interface Guest {
  id: string;
  email: string;
  full_name: string;
  phone_number?: string;
  pin?: string;
  pin_set_date?: string;
  pin_changed_by?: string;
  failed_pin_attempts?: number;
  account_locked_until?: string;
  status: string;
  created_at: string;
}

interface PinGenerationResult {
  success: boolean;
  pin?: string;
  error?: string;
}

interface BulkEmailResult {
  success: number;
  failed: number;
  errors: string[];
}

export const AdminPinManager: React.FC = () => {
  const [guests, setGuests] = useState<Guest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showPins, setShowPins] = useState(false);
  const [selectedGuests, setSelectedGuests] = useState<Set<string>>(new Set());
  
  // PIN generation dialog
  const [pinDialogOpen, setPinDialogOpen] = useState(false);
  const [pinDialogGuest, setPinDialogGuest] = useState<Guest | null>(null);
  const [customPin, setCustomPin] = useState('');
  const [pinGenerating, setPinGenerating] = useState(false);
  
  // Bulk email dialog
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [emailResults, setEmailResults] = useState<BulkEmailResult | null>(null);
  
  // Export dialog
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  
  // Copy feedback
  const [copySuccess, setCopySuccess] = useState<string | null>(null);

  useEffect(() => {
    loadGuests();
  }, []);

  const loadGuests = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const isDemoMode = (window as any).DEMO_MODE;
      
      if (isDemoMode) {
        // Demo data with PIN information
        const demoGuests: Guest[] = [
          {
            id: '1',
            email: 'sarah@example.com',
            full_name: 'Sarah Johnson',
            phone_number: '+1-555-0001',
            pin: '1234',
            pin_set_date: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
            pin_changed_by: 'admin',
            failed_pin_attempts: 0,
            status: 'active',
            created_at: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString()
          },
          {
            id: '2',
            email: 'mike@example.com',
            full_name: 'Mike Chen',
            phone_number: '+1-555-0002',
            pin: '5678',
            pin_set_date: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
            pin_changed_by: 'self',
            failed_pin_attempts: 1,
            status: 'active',
            created_at: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString()
          },
          {
            id: '3',
            email: 'alice@example.com',
            full_name: 'Alice Wilson',
            phone_number: '+1-555-0003',
            pin: undefined, // No PIN set yet
            failed_pin_attempts: 0,
            status: 'active',
            created_at: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString()
          },
          {
            id: '4',
            email: 'bob@example.com',
            full_name: 'Bob Thompson',
            phone_number: '+1-555-0004',
            pin: '9876',
            pin_set_date: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
            pin_changed_by: 'admin',
            failed_pin_attempts: 3,
            account_locked_until: new Date(Date.now() + 30 * 60 * 1000).toISOString(), // Locked for 30 minutes
            status: 'active',
            created_at: new Date(Date.now() - 20 * 24 * 60 * 60 * 1000).toISOString()
          }
        ];
        setGuests(demoGuests);
        setLoading(false);
        return;
      }

      // Use ApiService with demo fallback
      const data = await ApiService.getGuestsWithPins();
      if (data.success) {
        setGuests(data.data.guests || []);
      } else {
        throw new Error(data.error || 'Failed to load guests');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load guests');
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePin = async (guest: Guest, customPin?: string) => {
    setPinGenerating(true);
    try {
      const isDemoMode = (window as any).DEMO_MODE;
      
      if (isDemoMode) {
        // Demo mode - simulate PIN generation
        const newPin = customPin || Math.floor(1000 + Math.random() * 9000).toString();
        
        // Update local state
        setGuests(prevGuests => 
          prevGuests.map(g => 
            g.id === guest.id 
              ? { 
                  ...g, 
                  pin: newPin, 
                  pin_set_date: new Date().toISOString(),
                  pin_changed_by: 'admin'
                }
              : g
          )
        );
        
        setPinDialogOpen(false);
        setCustomPin('');
        return;
      }

      // Real API call
      const endpoint = customPin ? '/api/admin/set-pin' : '/api/admin/generate-pin';
      const body = customPin 
        ? { guestId: guest.id, pin: customPin }
        : { guestId: guest.id };

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      if (data.success) {
        // Reload guests to get updated PIN information
        await loadGuests();
        setPinDialogOpen(false);
        setCustomPin('');
      } else {
        throw new Error(data.error || 'Failed to generate PIN');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate PIN');
    } finally {
      setPinGenerating(false);
    }
  };

  const handleBulkEmailPins = async () => {
    setEmailSending(true);
    setEmailResults(null);
    
    try {
      const guestsToEmail = guests.filter(g => 
        selectedGuests.has(g.id) && g.pin && g.email
      );

      if (guestsToEmail.length === 0) {
        setError('No guests with PINs selected for email');
        return;
      }

      const isDemoMode = (window as any).DEMO_MODE;
      
      if (isDemoMode) {
        // Demo mode - simulate email sending
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        setEmailResults({
          success: guestsToEmail.length,
          failed: 0,
          errors: []
        });
        return;
      }

      // Real API call
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api';
      const response = await fetch(`${API_BASE_URL}/admin/bulk-email-pins`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          guestIds: Array.from(selectedGuests)
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      if (data.success) {
        setEmailResults(data.data.results);
      } else {
        throw new Error(data.error || 'Failed to send emails');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send emails');
    } finally {
      setEmailSending(false);
    }
  };

  const handleUnlockAccount = async (guest: Guest) => {
    try {
      const isDemoMode = (window as any).DEMO_MODE;
      
      if (isDemoMode) {
        // Demo mode - simulate account unlock
        setGuests(prevGuests => 
          prevGuests.map(g => 
            g.id === guest.id 
              ? { 
                  ...g, 
                  account_locked_until: undefined,
                  failed_pin_attempts: 0
                }
              : g
          )
        );
        return;
      }

      // Real API call
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api';
      const response = await fetch(`${API_BASE_URL}/admin/unlock-account`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({ guestId: guest.id })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      if (data.success) {
        await loadGuests();
      } else {
        throw new Error(data.error || 'Failed to unlock account');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to unlock account');
    }
  };

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(label);
      setTimeout(() => setCopySuccess(null), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  const handleSelectGuest = (guestId: string, selected: boolean) => {
    const newSelected = new Set(selectedGuests);
    if (selected) {
      newSelected.add(guestId);
    } else {
      newSelected.delete(guestId);
    }
    setSelectedGuests(newSelected);
  };

  const handleSelectAll = (selected: boolean) => {
    if (selected) {
      const guestsWithPins = guests.filter(g => g.pin).map(g => g.id);
      setSelectedGuests(new Set(guestsWithPins));
    } else {
      setSelectedGuests(new Set());
    }
  };

  const exportGuestPins = async (format: 'csv' | 'json' = 'csv', includeSecurityInfo: boolean = false) => {
    try {
      const isDemoMode = (window as any).DEMO_MODE;
      
      if (isDemoMode) {
        // Demo mode - generate local export
        const guestsWithPins = guests.filter(g => g.pin);
        
        if (format === 'json') {
          const jsonData = {
            exportDate: new Date().toISOString(),
            totalGuests: guestsWithPins.length,
            guests: guestsWithPins.map(g => ({
              name: g.full_name,
              email: g.email,
              pin: g.pin,
              phone: g.phone_number || '',
              pinSetDate: g.pin_set_date ? new Date(g.pin_set_date).toLocaleDateString() : '',
              setBy: g.pin_changed_by || '',
              ...(includeSecurityInfo && {
                failedAttempts: g.failed_pin_attempts || 0,
                accountLocked: isAccountLocked(g)
              })
            }))
          };
          
          const blob = new Blob([JSON.stringify(jsonData, null, 2)], { type: 'application/json' });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `guest-pins-${new Date().toISOString().split('T')[0]}.json`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
        } else {
          // CSV format
          const headers = ['Name', 'Email', 'PIN', 'Phone', 'Set Date', 'Set By'];
          if (includeSecurityInfo) {
            headers.push('Failed Attempts', 'Account Status');
          }
          
          const csvContent = [
            headers.join(','),
            ...guestsWithPins.map(g => {
              const row = [
                `"${g.full_name}"`,
                `"${g.email}"`,
                `"${g.pin}"`,
                `"${g.phone_number || ''}"`,
                `"${g.pin_set_date ? new Date(g.pin_set_date).toLocaleDateString() : ''}"`,
                `"${g.pin_changed_by || ''}"`
              ];
              
              if (includeSecurityInfo) {
                row.push(
                  `"${g.failed_pin_attempts || 0}"`,
                  `"${isAccountLocked(g) ? 'Locked' : 'Active'}"`
                );
              }
              
              return row.join(',');
            })
          ].join('\n');

          const blob = new Blob([csvContent], { type: 'text/csv' });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `guest-pins-${new Date().toISOString().split('T')[0]}.csv`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
        }
        
        setExportDialogOpen(false);
        return;
      }

      // Real API call
      const params = new URLSearchParams({
        format,
        includePersonalInfo: 'true',
        includeSecurityInfo: includeSecurityInfo.toString(),
        onlyGuestsWithPins: 'true'
      });

      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api';
      const response = await fetch(`${API_BASE_URL}/admin/export-pins?${params}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // Get filename from response headers
      const contentDisposition = response.headers.get('Content-Disposition');
      const filename = contentDisposition 
        ? contentDisposition.split('filename=')[1]?.replace(/"/g, '')
        : `guest-pins-${new Date().toISOString().split('T')[0]}.${format}`;

      // Download the file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      setExportDialogOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to export guest PINs');
    }
  };

  const printGuestList = async (includePhoneNumbers: boolean = true, includeSetDates: boolean = true) => {
    try {
      const isDemoMode = (window as any).DEMO_MODE;
      
      if (isDemoMode) {
        // Demo mode - generate local printable content
        const guestsWithPins = guests.filter(g => g.pin);
        const printContent = `
          <html>
            <head>
              <title>Guest PIN List</title>
              <style>
                body { font-family: Arial, sans-serif; margin: 20px; color: #333; }
                .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #e91e63; padding-bottom: 20px; }
                h1 { color: #e91e63; font-size: 28px; margin: 0; text-shadow: 1px 1px 2px rgba(0,0,0,0.1); }
                .subtitle { color: #666; font-size: 16px; margin-top: 10px; }
                .confidential { background: #fff3cd; border: 1px solid #ffeaa7; color: #856404; padding: 10px; border-radius: 4px; font-weight: bold; text-align: center; margin-bottom: 20px; }
                table { width: 100%; border-collapse: collapse; margin-top: 20px; box-shadow: 0 2px 8px rgba(0,0,0,0.1); }
                th, td { border: 1px solid #ddd; padding: 12px; text-align: left; }
                th { background: linear-gradient(135deg, #e91e63, #ad1457); color: white; font-weight: bold; text-shadow: 1px 1px 2px rgba(0,0,0,0.3); }
                tr:nth-child(even) { background-color: #f9f9f9; }
                .pin { font-family: 'Courier New', monospace; font-size: 18px; font-weight: bold; color: #e91e63; background: white; padding: 4px 8px; border-radius: 4px; border: 2px solid #e91e63; }
                .phone { color: #666; font-size: 14px; }
                .date { color: #888; font-size: 12px; }
                .footer { margin-top: 40px; text-align: center; color: #666; font-size: 12px; border-top: 1px solid #ddd; padding-top: 20px; }
                @media print { body { margin: 0; } }
              </style>
            </head>
            <body>
              <div class="header">
                <h1>🎉 Birthday Party - Guest PIN List 🎉</h1>
                <div class="subtitle">Generated on ${new Date().toLocaleString()}</div>
              </div>
              
              <div class="confidential">
                ⚠️ CONFIDENTIAL - FOR PARTY ORGANIZER USE ONLY ⚠️
              </div>
              
              <p><strong>Summary:</strong> Total Guests with PINs: ${guestsWithPins.length}</p>
              
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>PIN</th>
                    ${includePhoneNumbers ? '<th>Phone</th>' : ''}
                    ${includeSetDates ? '<th>PIN Set Date</th>' : ''}
                  </tr>
                </thead>
                <tbody>
                  ${guestsWithPins.map(g => `
                    <tr>
                      <td><strong>${g.full_name}</strong></td>
                      <td>${g.email}</td>
                      <td><span class="pin">${g.pin}</span></td>
                      ${includePhoneNumbers ? `<td class="phone">${g.phone_number || '-'}</td>` : ''}
                      ${includeSetDates ? `<td class="date">${g.pin_set_date ? new Date(g.pin_set_date).toLocaleDateString() : '-'}</td>` : ''}
                    </tr>
                  `).join('')}
                </tbody>
              </table>
              
              <div class="footer">
                <p><strong>Important:</strong> Keep this list secure and do not share PINs with unauthorized individuals.</p>
                <p>Generated by Birthday Party Management System</p>
              </div>
            </body>
          </html>
        `;

        const printWindow = window.open('', '_blank');
        if (printWindow) {
          printWindow.document.write(printContent);
          printWindow.document.close();
          printWindow.print();
        }
        setExportDialogOpen(false);
        return;
      }

      // Real API call
      const params = new URLSearchParams({
        title: 'Birthday Party - Guest PIN List',
        includePhoneNumbers: includePhoneNumbers.toString(),
        includeSetDates: includeSetDates.toString(),
        groupByStatus: 'false'
      });

      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api';
      const response = await fetch(`${API_BASE_URL}/admin/printable-guest-list?${params}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const htmlContent = await response.text();
      
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(htmlContent);
        printWindow.document.close();
        printWindow.print();
      }
      
      setExportDialogOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate printable guest list');
    }
  };

  const isAccountLocked = (guest: Guest) => {
    return guest.account_locked_until && new Date(guest.account_locked_until) > new Date();
  };

  const guestsWithPins = guests.filter(g => g.pin);
  const selectedGuestsWithPins = Array.from(selectedGuests).filter(id => 
    guests.find(g => g.id === id)?.pin
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h5">
          PIN Management
        </Typography>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadGuests}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            startIcon={showPins ? <VisibilityOffIcon /> : <VisibilityIcon />}
            onClick={() => setShowPins(!showPins)}
          >
            {showPins ? 'Hide PINs' : 'Show PINs'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={() => setExportDialogOpen(true)}
            disabled={guestsWithPins.length === 0}
          >
            Export
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {copySuccess && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {copySuccess} copied to clipboard!
        </Alert>
      )}

      {/* Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="primary">
                {guests.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Guests
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="success.main">
                {guestsWithPins.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Guests with PINs
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="warning.main">
                {guests.filter(g => g.failed_pin_attempts && g.failed_pin_attempts > 0).length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Failed Attempts
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="error.main">
                {guests.filter(g => isAccountLocked(g)).length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Locked Accounts
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Bulk Actions */}
      {guestsWithPins.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Bulk Actions
            </Typography>
            <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedGuests.size === guestsWithPins.length && guestsWithPins.length > 0}
                    indeterminate={selectedGuests.size > 0 && selectedGuests.size < guestsWithPins.length}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                  />
                }
                label={`Select All (${selectedGuests.size} selected)`}
              />
              <Button
                variant="contained"
                startIcon={<EmailIcon />}
                onClick={() => setEmailDialogOpen(true)}
                disabled={selectedGuestsWithPins.length === 0}
              >
                Email PINs ({selectedGuestsWithPins.length})
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Guests Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={selectedGuests.size === guestsWithPins.length && guestsWithPins.length > 0}
                  indeterminate={selectedGuests.size > 0 && selectedGuests.size < guestsWithPins.length}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                />
              </TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>PIN</TableCell>
              <TableCell>PIN Status</TableCell>
              <TableCell>Security</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {guests.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                    No guests found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              guests.map((guest) => (
                <TableRow key={guest.id}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedGuests.has(guest.id)}
                      onChange={(e) => handleSelectGuest(guest.id, e.target.checked)}
                      disabled={!guest.pin}
                    />
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {guest.full_name}
                      </Typography>
                      {guest.phone_number && (
                        <Typography variant="caption" color="text.secondary">
                          {guest.phone_number}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{guest.email}</TableCell>
                  <TableCell>
                    {guest.pin ? (
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontFamily: 'monospace', 
                            fontSize: '16px',
                            fontWeight: 'bold',
                            color: showPins ? 'text.primary' : 'transparent',
                            textShadow: showPins ? 'none' : '0 0 8px rgba(0,0,0,0.5)',
                            userSelect: showPins ? 'text' : 'none'
                          }}
                        >
                          {showPins ? guest.pin : '••••'}
                        </Typography>
                        {showPins && (
                          <Tooltip title={copySuccess === `PIN for ${guest.full_name}` ? 'Copied!' : 'Copy PIN'}>
                            <IconButton
                              size="small"
                              onClick={() => copyToClipboard(guest.pin!, `PIN for ${guest.full_name}`)}
                            >
                              <CopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    ) : (
                      <Chip label="No PIN" size="small" color="warning" />
                    )}
                  </TableCell>
                  <TableCell>
                    <Box display="flex" flexDirection="column" gap={0.5}>
                      {guest.pin && (
                        <>
                          <Typography variant="caption" color="text.secondary">
                            Set: {guest.pin_set_date ? new Date(guest.pin_set_date).toLocaleDateString() : 'Unknown'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            By: {guest.pin_changed_by || 'Unknown'}
                          </Typography>
                        </>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" flexDirection="column" gap={0.5}>
                      {guest.failed_pin_attempts && guest.failed_pin_attempts > 0 && (
                        <Chip 
                          label={`${guest.failed_pin_attempts} failed attempts`} 
                          size="small" 
                          color="warning" 
                        />
                      )}
                      {isAccountLocked(guest) && (
                        <Chip 
                          label="Account Locked" 
                          size="small" 
                          color="error"
                          icon={<LockIcon />}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={1}>
                      <Tooltip title={guest.pin ? 'Regenerate PIN' : 'Generate PIN'}>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setPinDialogGuest(guest);
                            setPinDialogOpen(true);
                          }}
                          color={guest.pin ? 'primary' : 'success'}
                        >
                          <RefreshIcon />
                        </IconButton>
                      </Tooltip>
                      {isAccountLocked(guest) && (
                        <Tooltip title="Unlock Account">
                          <IconButton
                            size="small"
                            onClick={() => handleUnlockAccount(guest)}
                            color="warning"
                          >
                            <LockOpenIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* PIN Generation Dialog */}
      <Dialog open={pinDialogOpen} onClose={() => setPinDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {pinDialogGuest?.pin ? 'Regenerate PIN' : 'Generate PIN'} - {pinDialogGuest?.full_name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              You can either generate a random PIN or set a custom 4-digit PIN.
            </Typography>
            
            <TextField
              fullWidth
              label="Custom PIN (optional)"
              value={customPin}
              onChange={(e) => setCustomPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
              placeholder="Leave empty to generate random PIN"
              inputProps={{ maxLength: 4 }}
              helperText="Enter exactly 4 digits, or leave empty for random generation"
              sx={{ mb: 2 }}
            />
            
            {customPin && customPin.length !== 4 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                PIN must be exactly 4 digits
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPinDialogOpen(false)} disabled={pinGenerating}>
            Cancel
          </Button>
          <Button
            onClick={() => handleGeneratePin(pinDialogGuest!, customPin || undefined)}
            variant="contained"
            disabled={Boolean(pinGenerating || (customPin && customPin.length !== 4))}
            startIcon={pinGenerating ? <CircularProgress size={20} /> : <RefreshIcon />}
          >
            {pinGenerating ? 'Generating...' : (customPin ? 'Set PIN' : 'Generate PIN')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Email Dialog */}
      <Dialog open={emailDialogOpen} onClose={() => setEmailDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Send PIN Emails</DialogTitle>
        <DialogContent>
          {emailSending ? (
            <Box display="flex" flexDirection="column" alignItems="center" gap={2} sx={{ py: 4 }}>
              <CircularProgress />
              <Typography>Sending emails...</Typography>
              <LinearProgress sx={{ width: '100%' }} />
            </Box>
          ) : emailResults ? (
            <Box sx={{ pt: 1 }}>
              <Alert severity={emailResults.failed === 0 ? 'success' : 'warning'} sx={{ mb: 2 }}>
                <Typography variant="h6">
                  Email Results
                </Typography>
                <Typography>
                  Successfully sent: {emailResults.success}
                </Typography>
                <Typography>
                  Failed: {emailResults.failed}
                </Typography>
              </Alert>
              
              {emailResults.errors.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" color="error" gutterBottom>
                    Errors:
                  </Typography>
                  <List dense>
                    {emailResults.errors.map((error, index) => (
                      <ListItem key={index}>
                        <ListItemText primary={error} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </Box>
          ) : (
            <Box sx={{ pt: 1 }}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Send PIN emails to {selectedGuestsWithPins.length} selected guests?
              </Typography>
              
              <List>
                {guests
                  .filter(g => selectedGuests.has(g.id) && g.pin)
                  .map(guest => (
                    <ListItem key={guest.id}>
                      <ListItemIcon>
                        <EmailIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary={guest.full_name}
                        secondary={guest.email}
                      />
                    </ListItem>
                  ))}
              </List>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setEmailDialogOpen(false);
            setEmailResults(null);
          }}>
            {emailResults ? 'Close' : 'Cancel'}
          </Button>
          {!emailResults && !emailSending && (
            <Button
              onClick={handleBulkEmailPins}
              variant="contained"
              startIcon={<SendIcon />}
              disabled={selectedGuestsWithPins.length === 0}
            >
              Send Emails
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Export Dialog */}
      <Dialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Export Guest PINs</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Choose how you'd like to export the guest PIN information:
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    📊 Data Export
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Download guest PIN data in various formats
                  </Typography>
                  <Box display="flex" flexDirection="column" gap={1}>
                    <Button
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      onClick={() => exportGuestPins('csv', false)}
                      fullWidth
                    >
                      Download CSV (Basic)
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      onClick={() => exportGuestPins('csv', true)}
                      fullWidth
                    >
                      Download CSV (with Security Info)
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      onClick={() => exportGuestPins('json', true)}
                      fullWidth
                    >
                      Download JSON (Complete)
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    🖨️ Printable Formats
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Generate printable guest lists for party organizers
                  </Typography>
                  <Box display="flex" flexDirection="column" gap={1}>
                    <Button
                      variant="outlined"
                      startIcon={<PrintIcon />}
                      onClick={() => printGuestList(true, true)}
                      fullWidth
                    >
                      Print Full List
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<PrintIcon />}
                      onClick={() => printGuestList(false, false)}
                      fullWidth
                    >
                      Print Names & PINs Only
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
          
          <Alert severity="warning" sx={{ mt: 3 }}>
            <Typography variant="body2">
              <strong>🔒 Security Notice:</strong> This export contains sensitive PIN information. 
              Please handle with care, store securely, and delete after use. Only share with authorized party organizers.
            </Typography>
          </Alert>
          
          <Alert severity="info" sx={{ mt: 1 }}>
            <Typography variant="body2">
              <strong>💡 Tip:</strong> Use the basic CSV export for simple guest lists, or the complete JSON export for backup purposes. 
              Printable formats are perfect for physical reference during the party.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDialogOpen(false)}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};